import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetChatConversationQuery } from "@/services/(my-space)/api.chat";
import { chatHub } from "@/services/websocket/chat-hub";
import { MS_CHAT_PATHS } from "@/routes/my-space/routes.chat";
import { cn } from "@/utils/cn";
import { ChatAvatar } from "@/pages/(home)/(my-space)/chat/_components/ChatAvatar";
import { ChatRoom } from "@/pages/(home)/(my-space)/chat/_components/ChatRoom";
import { conversationTitle } from "@/pages/(home)/(my-space)/chat/_lib/chat-labels";
import {
  CHAT_WINDOWS_MIN_WIDTH,
  closeChatWindow,
  toggleChatWindow,
  useChatWindows,
  type ChatWindowState,
} from "./chat-windows-store";
import { channelOf } from "@/pages/(home)/(my-space)/chat/_lib/channel-notices";

const WINDOW_WIDTH = 360;
const GAP = 12;
const EDGE = 16;

/** How many windows fit across the viewport (at least one on a wide-enough screen). */
const useFitCount = () => {
  const measure = () =>
    typeof window === "undefined" || window.innerWidth < CHAT_WINDOWS_MIN_WIDTH
      ? 0
      : Math.max(1, Math.floor((window.innerWidth - 2 * EDGE + GAP) / (WINDOW_WIDTH + GAP)));
  const [fit, setFit] = useState(measure);
  useEffect(() => {
    const onResize = () => setFit(measure());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return fit;
};

/**
 * Chats docked at the bottom edge, usable from any page (like LinkedIn's):
 * newest at the corner, each minimisable to its header, expandable to the
 * Chat page. Not shown on the Chat page itself — it's all there already.
 */
export const ChatWindows = () => {
  const windows = useChatWindows();
  const { pathname } = useLocation();
  const fit = useFitCount();
  const onChatPage =
    pathname === MS_CHAT_PATHS.ROOT || pathname.startsWith(`${MS_CHAT_PATHS.ROOT}/`);
  if (onChatPage || fit === 0 || windows.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed bottom-0 z-40 flex flex-row-reverse items-end gap-3"
      style={{ insetInlineEnd: EDGE }}
    >
      {windows.slice(0, fit).map((w) => (
        <ChatWindow key={w.conversationId} state={w} />
      ))}
    </div>
  );
};

const ChatWindow = ({ state: { conversationId, minimized } }: { state: ChatWindowState }) => {
  const { t } = useTranslation("chat");
  const navigate = useNavigate();
  const detail = useGetChatConversationQuery(conversationId);
  // A chat I left or that was deleted can't be shown: drop its window.
  const gone = detail.isError;
  useEffect(() => {
    if (gone) closeChatWindow(conversationId);
  }, [gone, conversationId]);

  const c = detail.data;
  const title = c ? conversationTitle(t, c) : "";
  const expand = () => {
    closeChatWindow(conversationId);
    navigate(MS_CHAT_PATHS.CONVERSATION(conversationId));
  };

  const controls = (
    <div className="flex shrink-0 items-center">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => toggleChatWindow(conversationId)}
        aria-label={minimized ? t("window.restore") : t("window.minimize")}
        title={minimized ? t("window.restore") : t("window.minimize")}
      >
        {minimized ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={expand}
        aria-label={t("window.expand")}
        title={t("window.expand")}
      >
        <Maximize2 className="size-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => closeChatWindow(conversationId)}
        aria-label={t("window.close")}
        title={t("window.close")}
      >
        <X className="size-4" />
      </Button>
    </div>
  );

  return (
    <section
      aria-label={t("window.label", { name: title })}
      className={cn(
        "pointer-events-auto flex flex-col overflow-hidden rounded-t-xl border border-b-0 border-border bg-background shadow-2xl",
        minimized ? "h-12" : "h-[min(560px,calc(100vh-5rem))]",
      )}
      style={{ width: WINDOW_WIDTH }}
    >
      {minimized ? (
        // Collapsed: just who it is; a click brings it back.
        <div className="flex h-12 items-center gap-2 ps-2.5 pe-1">
          <button
            type="button"
            onClick={() => toggleChatWindow(conversationId)}
            className="flex min-w-0 flex-1 items-center gap-2 text-start"
          >
            <ChatAvatar
              name={title}
              imageUrl={c?.type === "direct" ? c?.peer?.profileImage : c?.imageUrl}
              kind={c?.type}
              channel={channelOf(c)}
              size="sm"
              isOnline={
                c?.type === "direct" && !c.isSelf && !!chatHub.getPresence(c.peer?.id)?.online
              }
            />
            <span className="truncate text-sm font-semibold text-foreground">{title}</span>
            {c && c.unreadCount > 0 ? (
              <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                {c.unreadCount > 99 ? "99+" : c.unreadCount}
              </span>
            ) : null}
          </button>
          {controls}
        </div>
      ) : (
        <ChatRoom
          conversationId={conversationId}
          onBack={() => closeChatWindow(conversationId)}
          variant="window"
          headerActions={controls}
        />
      )}
    </section>
  );
};
